import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'

export default function MainLayout({ children }) {
  const location = useLocation()

  // Detectar tamaño de pantalla y establecer estado inicial
  const getInitialCollapsedState = () => {
    // Verificar si hay una preferencia guardada
    const savedPreference = localStorage.getItem('sidebarCollapsed')
    if (savedPreference !== null) {
      return savedPreference === 'true'
    }
    // Si no hay preferencia guardada, colapsar por defecto en pantallas pequeñas
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024 // lg breakpoint
    }
    return false
  }

  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialCollapsedState)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Solo suscripciones-clinicas (mapa vector mobile-like) es full-bleed en desktop.
  // /clinicas debe usar el contenedor normal; full-bleed rompe el grid/tabla.
  const isFullBleedRoute = useMemo(() => {
    return /^\/suscripciones-clinicas(\/|$)/.test(location.pathname)
  }, [location.pathname])

  // Detectar cambios en el tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      // En pantallas pequeñas, colapsar automáticamente si no hay preferencia guardada
      const savedPreference = localStorage.getItem('sidebarCollapsed')
      if (savedPreference === null) {
        if (window.innerWidth < 1024) {
          setSidebarCollapsed(true)
        } else {
          setSidebarCollapsed(false)
        }
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed
    setSidebarCollapsed(newState)
    // Guardar preferencia del usuario
    localStorage.setItem('sidebarCollapsed', String(newState))
  }

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50 text-gray-900 transition-colors dark:bg-gray-950 dark:text-gray-100">
      {/* Sidebar Desktop */}
      <div className={`hidden lg:flex lg:flex-shrink-0 transition-all duration-300 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}>
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden flex">
          <div
            className="fixed inset-0 bg-slate-900/40 transition-opacity"
            onClick={toggleMobileMenu} 
          />
          <div className="relative flex flex-col w-full max-w-xs bg-white dark:bg-gray-900 shadow-strong transform transition-transform h-full">
            <Sidebar collapsed={false} onToggle={toggleMobileMenu} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        {!isFullBleedRoute && (
          <Header 
            onMenuToggle={toggleMobileMenu}
            sidebarCollapsed={sidebarCollapsed}
          />
        )}

        {/* Page content */}
        <main className={`flex-1 ${isFullBleedRoute ? 'overflow-hidden bg-white dark:bg-gray-950' : 'overflow-y-auto bg-gray-50 transition-colors dark:bg-gray-950'}`}>
          {isFullBleedRoute ? (
            children
          ) : (
            <div className="py-6 px-4 lg:px-6">
              <div className="max-w-7xl mx-auto min-w-0">
                {children}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
